// Browser Desk console: what the page derives from the API, with no React in it (jest covers it).
//
// Every field the redesign added to the API is optional here: an API from before it sends none
// of desks_status, morning, limits, office_desk, override_jobs, stop_today, probation_days, and
// each helper then returns what the page showed before (or null, and the piece hides).

export const OK = '#2e7d32';
export const WARN = '#ed6c02';
export const BAD = '#c62828';
export const GREY = '#757575';

export const WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const SUN_FIRST = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const DAY_MS = 24 * 3600 * 1000;
export const DRIFT_DAYS = 14;   // a console override older than this asks for a PR into credentials.yaml
export const STREAK_MAX = 4;    // failed sign-ins in a row before the desk turns a login off
export const LIMIT_DEFAULTS = { successful_logins_per_day: 10, login_attempts_per_day: 3, max_runs_per_day: 2 };

export const fmtTime = (iso, tz) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString([], { timeZone: tz, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
        return iso;
    }
};
export const fmtClock = (iso, tz) => {
    try {
        return new Date(iso).toLocaleTimeString([], { timeZone: tz, hour: 'numeric', minute: '2-digit' });
    } catch {
        return iso;
    }
};
// 'Oct 14' from '2026-10-14' (a calendar day, so no timezone shift).
export const fmtDay = (day) => {
    try {
        return new Date(`${day}T12:00:00Z`).toLocaleDateString([], { timeZone: 'UTC', month: 'short', day: 'numeric' });
    } catch {
        return day;
    }
};

export const daysText = (days) => (days.length === 7 ? 'daily'
    : days.join(',') === 'mon,tue,wed,thu,fri' ? 'weekdays' : days.join(', '));
export const weekdayOf = (isoDay) => SUN_FIRST[new Date(`${isoDay}T12:00:00Z`).getUTCDay()];
const mondayOf = (isoDay) => {
    const d = new Date(`${isoDay}T12:00:00Z`);
    return new Date(d.getTime() - ((d.getUTCDay() + 6) % 7) * DAY_MS).toISOString().slice(0, 10);
};

// The schedule in effect: one set in this console wins over the desk's registry file.
export const scheduleOf = (row) => {
    const o = row.schedule_override;
    return { days: o?.days || row.days || [], window: o?.window || row.window || {}, custom: Boolean(o), override: o };
};

export const needsEnroll = (row) => row.enabled && row.enrolled === false && !row.disabled && !row.fixing;
export const ENROLL_TIP = 'This login has never been signed in on the prod desk, so its scheduled runs are skipped. '
    + 'Enroll now signs it in with the stored credentials (if the vendor emails a code to ops@, the desk reads it), '
    + 'records the prod desk as this login\'s device, and stops: no files are pulled. Its scheduled runs start on '
    + 'their next run day. Do this once, right after the login goes live. Run now signs in and pulls files.';

// Probation after go-live: three clean run days in a row (worker's 06:20 ET check).
export const probationOf = (p) => {
    if (!p) return null;
    if (p.passed_at) {
        return { label: 'Probation passed', colour: OK, detail: `Passed ${p.passed_at.slice(0, 10)}: open the cleanup PR for ${(p.replaces || []).join(', ') || 'the old extractor'}` };
    }
    const note = p.last_note && p.last_note !== 'clean' ? ` · last run day ${p.last_day}: ${p.last_note}` : '';
    return { label: `Probation ${p.clean_in_a_row} of ${p.of}`, colour: GREY, detail: `Clean run days in a row since ${p.start}${note}` };
};

// ---- desks ---------------------------------------------------------------------------------

export const minutesSince = (iso, nowMs = Date.now()) => Math.max(0, Math.round((nowMs - new Date(iso).getTime()) / 60000));
export const deskNowText = (rec) => (rec
    ? `${rec.by} is running ${rec.action} ${rec.credential} · ${minutesSince(rec.started_at)} min`
      + (rec.stale ? ' (no heartbeat lately: probably left over from a restart)' : '')
    : 'idle');

// Prod desk 0 is "prod" (pod browser-desk-0), desk N is "prod-N"; dev is "dev".
export const prodOrdinal = (name) => (name === 'prod' ? 0 : /^prod-\d+$/.test(name) ? Number(name.slice(5)) : null);
export const prodName = (n) => (n ? `prod-${n}` : 'prod');
export const deskLabel = (name) => (prodOrdinal(name) !== null ? `Prod desk ${prodOrdinal(name)}` : 'Dev');
export const deskPod = (name) => (prodOrdinal(name) !== null ? `browser-desk-${prodOrdinal(name)}` : 'browser-desk-dev-0');
export const loginDesk = (row) => row.desk || 0;

// The API lists desks in order (desk_list); an API from before prod sharding only sends the
// desks map. Either way a desk a login names is shown even if the list missed it.
export const deskList = (data) => {
    const listed = data?.desk_list?.length ? data.desk_list
        : Object.keys(data?.desks || {}).map((name) => ({ name, pod: deskPod(name), prod_desk: prodOrdinal(name) }));
    const byName = new Map(listed.map((d) => [d.name, d]));
    for (const n of [0, ...(data?.logins || []).map(loginDesk)]) {
        if (!byName.has(prodName(n))) byName.set(prodName(n), { name: prodName(n), pod: deskPod(prodName(n)), prod_desk: n });
    }
    const prods = [...byName.values()].filter((d) => prodOrdinal(d.name) !== null)
        .sort((a, b) => prodOrdinal(a.name) - prodOrdinal(b.name));
    return [...prods, byName.get('dev') || { name: 'dev', pod: deskPod('dev'), prod_desk: null }];
};
export const deskOrder = (data) => deskList(data).map((d) => d.name);

// Office desks (a Mac in the office, not a cluster pod): top-level office_desks, else none known.
export const officeDesks = (data) => (Array.isArray(data?.office_desks) ? data.office_desks : []);
// Decision 2: Test runs only for logins on an office desk. Unknown (older API) = not offered.
export const isOffice = (row, data) => row.office_desk === true
    || (row.office_desk === undefined && officeDesks(data).includes(loginDesk(row)));

// Desk strip entries: the desk list, each with its heartbeat (desks_status) when the API sends
// one. A heartbeat for a desk the list doesn't know is shown too.
export const deskStrip = (data) => {
    const beats = Array.isArray(data?.desks_status) ? data.desks_status : [];
    const offices = officeDesks(data);
    const entries = deskList(data).map((d) => {
        const hb = beats.find((b) => b?.name === d.name) || null;
        return { ...d, status: hb, office: hb?.kind === 'office' || (d.prod_desk !== null && offices.includes(d.prod_desk)) };
    });
    beats.filter((b) => b?.name && !entries.some((e) => e.name === b.name)).forEach((b) => {
        entries.splice(entries.length - 1, 0, { name: b.name, pod: b.host, prod_desk: prodOrdinal(b.name), status: b, office: b.kind === 'office' });
    });
    return entries;
};

export const agoText = (iso, nowMs) => {
    const s = Math.max(0, Math.round((nowMs - new Date(iso).getTime()) / 1000));
    if (Number.isNaN(s)) return '';
    if (s < 90) return `${s} s ago`;
    if (s < 90 * 60) return `${Math.round(s / 60)} min ago`;
    return `${Math.round(s / 3600)} h ago`;
};
// One heartbeat's lines: { version, versionNote, beat, stale }, or null with no heartbeat.
export const heartbeatText = (hb, nowMs) => {
    if (!hb) return null;
    const v = hb.version ? String(hb.version) : null;
    return {
        version: v ? `${hb.kind === 'office' ? 'Bundle' : 'Code'} ${v.length > 12 ? v.slice(0, 8) : v}` : null,
        versionNote: hb.restart_pending ? 'restarts onto new code when idle' : v ? 'current' : null,
        beat: hb.at ? `Heartbeat ${agoText(hb.at, nowMs)}` : 'No heartbeat yet',
        stale: Boolean(hb.stale),
    };
};

// ---- today --------------------------------------------------------------------------------

export const liveRunsToday = (row) => (row.today?.runs || []).filter((x) => !x.scratch);
export const testRunsToday = (row) => (row.today?.runs || []).filter((x) => x.scratch);

// When a login next runs today, or null: its planned start until it has run, then the desk's
// retry time. Off, not live, done, retries stopped or waiting for Enroll now: no run is coming.
export const pendingAt = (row) => {
    if (row.disabled || row.fixing || !row.enabled || row.today?.done || row.stop_today || needsEnroll(row)) return null;
    return (row.today?.runs || []).length ? row.today?.next || null : row.today?.start || null;
};

// The one status a person reads first. "Turned off" outranks everything. Test (scratch) runs
// land nothing live and never set it.
export const statusOf = (row, desks, order = ['prod', 'dev']) => {
    const runs = liveRunsToday(row);
    const last = runs[runs.length - 1];
    if (row.disabled) {
        const by = row.disabled.by || 'the desk';
        return { label: 'Turned off', colour: BAD, detail: `Turned off by ${by}: ${row.disabled.reason || row.disabled.page_class}` };
    }
    if (row.fixing) {
        return {
            label: 'Being fixed on dev', colour: WARN,
            detail: `Off on prod since ${(row.fixing.at || '').slice(0, 10)}: ${row.fixing.by} is fixing it on the dev desk`
                + ` (${row.fixing.reason}). Prod runs it again once it is released.`,
        };
    }
    for (const d of order) {
        const rec = desks?.[d];
        if (rec && !rec.stale && rec.credential === row.id) {
            return { label: d === 'dev' ? 'Running on dev' : 'Running now', colour: GREY, detail: `${deskLabel(d)}: ${deskNowText(rec)}` };
        }
    }
    if (needsEnroll(row)) return { label: `Not signed in on Prod desk ${row.desk || 0}`, colour: WARN, detail: ENROLL_TIP };
    if (row.today?.done && last?.status === 'completed') {
        return { label: 'Done today', colour: OK, detail: `Completed ${fmtTime(last.at, row.timezone)}` };
    }
    if (row.stop_today && !row.today?.done) {
        return { label: 'Retries stopped today', colour: GREY,
                 detail: `${row.stop_today.by || 'Someone'}: ${row.stop_today.reason || 'no reason given'}. Runs again on its next run day.` };
    }
    if (last && last.status !== 'completed') {
        const base = { deferred: 'Waiting to log in', partial: 'Some files missing', interrupted: 'Cut off, resuming' }[last.status]
            || `Last run ${last.status}`;
        const next = !row.today?.done && row.today?.next;
        const label = next ? `${base} · retries ${fmtTime(next, row.timezone)}` : base;
        const after = next
            ? `It runs again at ${fmtTime(next, row.timezone)} and fetches only what's missing.`
            : 'No more runs today; it runs again on its next run day (Run now still works).';
        return { label, colour: last.status === 'deferred' ? GREY : WARN, detail: [last.message, after].filter(Boolean).join(' ') };
    }
    if (!row.enabled) return { label: 'Not scheduled', colour: GREY, detail: 'Not live yet: built and tested on the dev desk' };
    if (row.today?.start) return { label: `Planned ${fmtTime(row.today.start, row.timezone)}`, colour: GREY, detail: '' };
    const sched = scheduleOf(row);
    if (row.today?.day && sched.days.length && !sched.days.includes(weekdayOf(row.today.day))) {
        return { label: 'Not a run day', colour: GREY, detail: `Runs ${daysText(sched.days)}; Run now still works` };
    }
    return { label: 'No plan yet', colour: GREY, detail: 'The desk plans each login when its day starts' };
};

// Last live run: today's last live plan entry, else the API's last_run when it is live, else the
// newest job in jobs_last. A test run is never it. null when none is known.
// Plan entries that are not a run (refused, waited, enroll-only, skipped, cut off) are never it.
const NOT_A_RUN = ['refused', 'deferred', 'enrolled', 'skipped', 'interrupted'];
const isLiveRun = (r) => r && !r.scratch && !NOT_A_RUN.includes(String(r.status || '').toLowerCase());
export const lastLiveRun = (row) => {
    const today = liveRunsToday(row).filter(isLiveRun);
    if (today.length) return { ...today[today.length - 1], day: row.today?.day };
    // newer API: the newest real run (completed/partial/failed/stopped); older: last_run, filtered
    if ('last_real_run' in row) {
        if (row.last_real_run && !row.last_real_run.scratch) return row.last_real_run;
    } else if (isLiveRun(row.last_run)) return row.last_run;
    const jobs = Object.values(row.jobs_last || {}).filter(Boolean).sort((a, b) => (a.at < b.at ? 1 : -1));
    return jobs[0] ? { at: jobs[0].at, status: jobs[0].status, day: jobs[0].day } : null;
};

// ---- sign-in budget -----------------------------------------------------------------------

export const budgetOf = (row, nowMs = Date.now()) => {
    const lt = row.logins_today || {};
    const limits = row.limits ? { ...LIMIT_DEFAULTS, ...row.limits } : null;
    const live = typeof row.runs_today_live === 'number' ? row.runs_today_live : liveRunsToday(row).length;
    const next = lt.next_allowed && new Date(lt.next_allowed).getTime() > nowMs ? lt.next_allowed : null;
    return {
        ok: lt.ok ?? 0, failed: lt.failed ?? 0, inProgress: lt.in_progress ?? 0,
        limits, live,
        liveLeft: limits ? Math.max(0, limits.max_runs_per_day - live) : null,
        streak: typeof lt.streak === 'number' ? lt.streak : null,
        nextAllowed: next,
        waitMin: next ? Math.max(1, Math.ceil((new Date(next).getTime() - nowMs) / 60000)) : 0,
    };
};

// ---- jobs ---------------------------------------------------------------------------------

const ownDays = (j) => (Array.isArray(j?.days) && j.days.length ? j.days : null);
// A person's name for a job: its data source when that alone tells the login's jobs apart, else
// job_name humanised ("Mason::ExtractAmiraWeekly" -> "Amira weekly", "Lexington::EasyCBMExtract" ->
// "Easy CBM"), with the client added when two jobs would still read the same.
export const humanise = (name, client) => {
    let n = String(name || '').trim();
    n = n.replace(/^[^:]*::/, '');
    if (client && n.toLowerCase().startsWith(`${client.toLowerCase()} `)) n = n.slice(client.length + 1);
    n = n.replace(/^extract(?=[A-Z_\s]|$)/i, '').replace(/([A-Za-z0-9])Extract$/, '$1').replace(/[_\s]+/g, ' ').trim();
    const words = n.split(' ').flatMap((w) => w.match(/[A-Z]+(?![a-z])|[A-Z]?[a-z0-9]+/g) || [w]);
    const out = words.map((w, i) => (i === 0 || /^[A-Z0-9]{2,}$/.test(w) ? w : w.toLowerCase())).join(' ');
    return out ? out[0].toUpperCase() + out.slice(1) : String(name || '');
};
const baseLabel = (job, row) => {
    const jobs = row?.jobs || [];
    if (job.data_source && jobs.filter((j) => j.data_source === job.data_source).length === 1) return job.data_source;
    return humanise(job.job_name || job.job_type, job.client);
};
export const jobLabel = (job, row) => {
    if (!job) return '';
    const label = baseLabel(job, row);
    const same = (row?.jobs || []).filter((j) => baseLabel(j, row) === label).length > 1;
    return same && job.client ? `${label} (${job.client})` : label;
};
export const jobName = (row, jt) => {
    const j = (row.jobs || []).find((x) => x.job_type === jt);
    return j ? jobLabel(j, row) : jt;
};

// The job days in effect: the console's per-job override, else the registry's, else the login's.
export const jobDaysOf = (row, job) => {
    const o = row.override_jobs?.[job.job_type];
    const login = scheduleOf(row).days;
    const days = (o?.days?.length ? o.days : ownDays(job)) || login;
    return { days: WEEK.filter((d) => days.includes(d)), catchUp: o ? o.catch_up || null : job.catch_up || null, override: o || null, own: Boolean(o?.days?.length || ownDays(job)) };
};

// The jobs due today: today.jobs when the plan names them, else every job on a run day, else none.
export const dueJobTypes = (row) => {
    const all = (row.jobs || []).map((j) => j.job_type);
    if (Array.isArray(row.today?.jobs)) return all.filter((jt) => row.today.jobs.includes(jt));
    const days = scheduleOf(row).days;
    if (row.today?.day && days.length && !days.includes(weekdayOf(row.today.day))) return [];
    return all;
};
const completedToday = (row, jt) => {
    const l = row.jobs_last?.[jt];
    return Boolean(l && l.day && l.day === row.today?.day && l.status === 'completed');
};

// Decision 1: Run now defaults to the jobs due today that haven't completed today.
export const defaultRunJobs = (row) => dueJobTypes(row).filter((jt) => !completedToday(row, jt));

// One line per job for the drawer and the Run now picker.
export const jobRows = (row, runs = []) => {
    const due = new Set(dueJobTypes(row));
    const monday = row.today?.day ? mondayOf(row.today.day) : null;
    return (row.jobs || []).map((j) => {
        const jd = jobDaysOf(row, j);
        const last = row.jobs_last?.[j.job_type] || null;
        const lastTest = runs.find((r) => r.scratch && (r.job_types || []).includes(j.job_type)) || null;
        // jobruns/ mark (newer API): Skipped or Completed on a day this ISO week covers the week
        const mark = row.jobs_mark?.[j.job_type] || null;
        const markThisWeek = Boolean(mark && monday && mark.last_completed_day && mark.last_completed_day >= monday
            && mark.last_completed_day <= row.today.day);
        const markStatus = markThisWeek ? String(mark.status || '').toLowerCase() : null;
        const skippedThisWeek = markStatus === 'skipped' ? { by: mark.by || null, reason: mark.reason || null } : null;
        const doneThisWeek = jd.catchUp === 'week' && (markStatus === 'completed' || Boolean(skippedThisWeek)
            || Boolean(last && monday && last.day >= monday && ['completed', 'skipped'].includes(String(last.status).toLowerCase())));
        return {
            job_type: j.job_type, name: jobLabel(j, row), source: j.data_source || row.vendor,
            daysText: (s => s[0].toUpperCase() + s.slice(1))(daysText(jd.days) || 'no days'),
            catchUp: jd.catchUp, override: jd.override, own: jd.own,
            dueToday: due.has(j.job_type), completedToday: completedToday(row, j.job_type),
            last, lastTest, doneThisWeek, skippedThisWeek,
        };
    });
};

// ---- lifecycle (drawer stepper) -----------------------------------------------------------

// Built → Tested on dev → Live → Signed in on prod → Probation (day dots) → Passed → Cleanup PR.
// Probation steps come from the worker's probation record; a login from before probation has
// none, so they stay grey. probation_days (newer API) adds a dot per counted run day.
export const lifecycleOf = (row) => {
    const p = row.probation;
    const map = row.probation_days && typeof row.probation_days === 'object' ? row.probation_days : null;
    const dots = map ? Object.keys(map).sort().map((day) => ({ day, note: map[day], clean: map[day] === 'clean' })) : [];
    const passed = Boolean(p?.passed_at);
    const signedIn = row.enabled && row.enrolled !== false;
    const steps = [
        { label: 'Built', state: 'done' },
        { label: 'Tested on dev', state: row.enabled ? 'done' : 'now' },
        { label: 'Live', state: row.enabled ? 'done' : 'todo' },
        { label: 'Signed in on prod', state: !row.enabled ? 'todo' : signedIn ? 'done' : 'now' },
        { label: p ? `Probation ${p.clean_in_a_row} of ${p.of}` : 'Probation', state: passed ? 'done' : p && signedIn ? 'now' : 'todo' },
        { label: passed ? `Passed ${p.passed_at.slice(0, 10)}` : 'Passed', state: passed ? 'done' : 'todo' },
        { label: 'Cleanup PR', state: passed ? 'now' : 'todo' },
    ];
    return { steps, dots, replaces: p?.replaces || row.replaces || [], noRecord: !p && signedIn };
};

// ---- run history --------------------------------------------------------------------------

export const RUN_LABEL = {
    completed: 'Completed', partial: 'Partial', failed: 'Failed', stopped: 'Stopped', deferred: 'Waited',
    interrupted: 'Cut off', refused: 'Refused',
};
export const runColour = (status) => ({ completed: OK, partial: WARN, failed: BAD, stopped: BAD, interrupted: WARN }[status] || GREY);

// Who started it: 'scheduled', or 'on request · eric' (requested_by is 'console:eric' / 'cli').
export const byText = (run) => {
    const by = run.by || run.requested_by;
    if (!by || by === 'schedule') return run.adhoc ? 'on request' : 'scheduled';
    const who = String(by).replace(/^console:/, '');
    return `on request · ${String(by).startsWith('console:') ? 'console · ' : ''}${who}`;
};

// A3's history ({runs} or a bare list), else today's plan entries. Newest first.
export const historyRuns = (resp, row) => {
    const fromApi = Array.isArray(resp) ? resp : Array.isArray(resp?.runs) ? resp.runs : null;
    const src = fromApi || (row.today?.runs || []).map((r) => ({ ...r, day: row.today?.day }));
    return src.map((r, i) => ({
        ...r,
        key: r.run_id || `${r.at}-${i}`,
        scratch: Boolean(r.scratch),
        status: String(r.status || '').toLowerCase(),
        job_types: Array.isArray(r.job_types) ? r.job_types : (r.jobs || []).map((j) => j.job_type).filter(Boolean),
        fromHistory: Boolean(fromApi),
    })).sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
};

export const canPromote = (run) => Boolean(run.fromHistory && run.scratch && run.run_id && run.status === 'completed');

// What a run fetched, per job: 'Amira daily: 11 files, 1 missing'.
export const runJobsText = (row, run) => (run.jobs || []).map((j) => {
    const parts = [];
    if (typeof j.files === 'number') parts.push(`${j.files} file${j.files === 1 ? '' : 's'}`);
    if (j.missing) parts.push(`${j.missing} missing`);
    const st = j.status ? String(j.status).toLowerCase() : '';
    return `${jobName(row, j.job_type)}: ${[st, ...parts].filter(Boolean).join(', ')}`;
});

// Screenshots grouped by run: A1's runs, else the flat list grouped by the run folder in the path
// (<scope><id>/<day>/<run_id>/<file>.png).
export const groupShots = (resp) => {
    const norm = (f) => (typeof f === 'string' ? { name: f, url: f } : f);
    if (Array.isArray(resp?.runs) && resp.runs.length) {
        return resp.runs.map((r) => ({ run_id: r.run_id, day: r.day, files: (r.files || []).map(norm) }));
    }
    const groups = new Map();
    (resp?.screenshots || []).forEach((s) => {
        const parts = String(s.name).split('/');
        const runId = parts.length >= 4 ? parts[parts.length - 2] : null;
        const day = parts.find((p) => /^\d{4}-\d{2}-\d{2}$/.test(p)) || null;
        const k = runId || day || 'other';
        if (!groups.has(k)) groups.set(k, { run_id: runId, day, files: [] });
        groups.get(k).files.push(s);
    });
    return [...groups.values()];
};

// ---- Run now ------------------------------------------------------------------------------

// Button text names the action: 'Test HMH', 'Run Amira daily', 'Run all 3 jobs', 'Retry 1 missing file'.
export const runButtonText = ({ scratch, names, total, missing = 0 }) => {
    if (!names.length) return 'Pick a job';
    if (scratch) return `Test ${names.join(' + ')}`;
    if (missing > 0 && names.length === 1) return `Retry ${missing} missing file${missing === 1 ? '' : 's'}`;
    if (total > 1 && names.length === total) return `Run all ${total} jobs`;
    return `Run ${names.join(' + ')}`;
};

// Missing files the preview knows about for the picked jobs: expected minus landed today, where
// expected is today's last live run's landed + missing (null when no live run today).
export const previewMissing = (preview, picked) => (preview?.jobs || [])
    .filter((j) => picked.includes(j.job_type))
    .reduce((n, j) => {
        const landed = new Set(j.landed_today || []);
        if (!Array.isArray(j.expected)) return n;
        return n + j.expected.filter((b) => !landed.has(b)).length;
    }, 0);

// "This will…" lines and a refusal, from the preview (A4) when the API has it, else from what
// /logins already says. { lines, refused, heads }.
export const runPreview = ({ row, picked, scratch, preview, nowMs = Date.now(), clientName }) => {
    const names = picked.map((jt) => jobName(row, jt));
    const b = budgetOf(row, nowMs);
    const lines = [];
    let refused = null;
    if (row.disabled) refused = 'It is turned off. Turn it on first.';
    else if (row.fixing) refused = 'It is being fixed on the dev desk; prod runs it again once it is released.';
    else if (!row.enabled && !scratch) refused = 'It is not live yet; only a test run is allowed.';
    if (!picked.length) return { lines: ['nothing yet: pick at least one job'], refused };
    const where = scratch ? 'writing to test storage only' : `writing to ${clientName || 'the client'}'s storage and running the transform`;
    lines.push(`run ${names.join(', ')}, ${where}`);
    if (preview) {
        (preview.jobs || []).filter((j) => picked.includes(j.job_type)).forEach((j) => {
            const landed = (j.landed_today || []).length;
            if (landed) lines.push(`skip ${jobName(row, j.job_type)}'s ${landed} file${landed === 1 ? '' : 's'} that landed today`);
            const miss = Array.isArray(j.missing_last_run) ? j.missing_last_run.length : 0;
            if (miss) lines.push(`retry ${miss} file${miss === 1 ? '' : 's'} of ${jobName(row, j.job_type)} that today's last run missed`);
        });
        const s = preview.sign_in || {};
        lines.push(s.wait_s > 0 && s.next_allowed
            ? `sign in once if the session ended: waits until ${fmtClock(s.next_allowed, row.timezone)} (sign-ins are spaced out; the wait is never skipped)`
            : 'sign in once if the session ended (next sign-in allowed now)');
        if (!scratch && typeof preview.live_runs_left === 'number') {
            const left = preview.live_runs_left;
            const max = b.limits?.max_runs_per_day;
            lines.push(left <= 0 ? 'use a live run: none left today'
                : max ? `use live run ${max - left + 1} of ${max} today` : `use 1 of the ${left} live run${left === 1 ? '' : 's'} left today`);
            if (left <= 0 && !refused && !preview.refused) refused = 'No live runs left today. A live run is refused until tomorrow.';
        }
        // the API's own refusal says why best
        if (preview.refused) refused = refused || String(preview.refused);
    } else {
        lines.push(b.nextAllowed
            ? `sign in once if the session ended: waits until ${fmtClock(b.nextAllowed, row.timezone)} (sign-ins are spaced out; the wait is never skipped)`
            : 'sign in once if the session ended; files that already landed today are skipped');
        if (!scratch && b.limits) {
            lines.push(b.liveLeft > 0 ? `use live run ${b.live + 1} of ${b.limits.max_runs_per_day} today` : 'use a live run: none left today');
        }
    }
    if (!scratch && b.limits && b.liveLeft === 0 && !refused && !preview) {
        refused = `Live runs today: ${b.live} of ${b.limits.max_runs_per_day}. A live run is refused until tomorrow${row.office_desk ? '; switch to Test, or wait for the next run day' : ''}.`;
    }
    lines.push(scratch ? 'not count as the day\'s run, probation or a completion'
        : 'mark the day done only if every due job completes');
    return { lines, refused };
};

// ---- Schedule -----------------------------------------------------------------------------

// The editor's starting point: login days/window plus each job's days in effect.
export const scheduleDraft = (row) => {
    const sc = scheduleOf(row);
    const jobs = {};
    (row.jobs || []).forEach((j) => {
        const jd = jobDaysOf(row, j);
        // inherit: no days of its own, so it follows the login's days as they change
        jobs[j.job_type] = { days: jd.days, catch_up: jd.catchUp, inherit: !jd.own };
    });
    return { days: WEEK.filter((d) => sc.days.includes(d)), start: sc.window.start || '08:30', end: sc.window.end || '10:30', jobs };
};

const sameDays = (a, b) => WEEK.filter((d) => a.includes(d)).join() === WEEK.filter((d) => b.includes(d)).join();

// What Save sends and says. Per-job days are clamped to the login's days; a job whose days and
// catch-up match the registry file is left out (no override for it).
export const scheduleChanges = (row, draft, withJobs) => {
    const start = scheduleDraft(row);
    const changes = [];
    if (!sameDays(draft.days, start.days)) changes.push(`Login → ${daysText(WEEK.filter((d) => draft.days.includes(d))) || 'no days'}`);
    if (draft.start !== start.start || draft.end !== start.end) changes.push(`Window → ${draft.start}–${draft.end}`);
    const jobs = {};
    if (withJobs) {
        (row.jobs || []).forEach((j) => {
            const d = draft.jobs[j.job_type] || { days: [], catch_up: null };
            const days = WEEK.filter((x) => d.days.includes(x) && draft.days.includes(x));
            const catchUp = d.catch_up || null;
            const was = start.jobs[j.job_type];
            const fileDays = WEEK.filter((x) => (ownDays(j) || draft.days).includes(x) && draft.days.includes(x));
            if (!sameDays(days, fileDays) || catchUp !== (j.catch_up || null)) jobs[j.job_type] = { days, catch_up: catchUp };
            const followsLogin = d.inherit && was.inherit;
            if ((!followsLogin && !sameDays(days, was.days)) || catchUp !== (was.catch_up || null)) {
                changes.push(`${jobLabel(j, row)} → ${daysText(days) || 'no days'}${catchUp === 'week' ? ', catch-up' : ''}`);
            }
        });
    }
    const payload = { days: WEEK.filter((d) => draft.days.includes(d)), window: { start: draft.start, end: draft.end } };
    if (withJobs) payload.jobs = jobs;
    return { changes, payload };
};

export const scheduleProblem = (draft, reason, withJobs) => {
    if (!draft.days.length) return 'Pick at least one day.';
    if (!(draft.start >= '06:00' && draft.start < draft.end && draft.end <= '19:00')) return 'The window must be daytime (06:00–19:00), start before end.';
    if (withJobs) {
        const empty = Object.entries(draft.jobs).find(([, j]) => !WEEK.some((d) => j.days.includes(d) && draft.days.includes(d)));
        if (empty) return 'Every job needs at least one of the login\'s days.';
    }
    if (!reason.trim()) return 'Say why: what the vendor\'s data does on these days.';
    return null;
};

// Decision 3: overrides never expire; after DRIFT_DAYS the page asks for a PR.
export const driftNotice = (row, nowMs = Date.now()) => {
    const jobs = row.override_jobs && Object.keys(row.override_jobs).length;
    if (!row.schedule_override && !jobs) return null;
    const since = row.override_since || row.schedule_override?.at;
    if (!since) return null;
    if (nowMs - new Date(since).getTime() < DRIFT_DAYS * DAY_MS) return null;
    return `Differs from the file since ${fmtDay(since.slice(0, 10))}: open a PR to move it into Desk/credentials.yaml.`;
};

// ---- needs a look (banner) ----------------------------------------------------------------

// The 06:20 check's results ({job: {download, transform, missing}}) as one problem, or null.
// Its statuses are capitalised ("Completed", "Partial"); completed / not due / skipped are fine.
const MORNING_OK = ['completed', 'not due', 'skipped'];
export const morningProblem = (m, row) => {
    const nm = (jt) => (row ? jobName(row, jt) : jt);
    const res = m?.results && typeof m.results === 'object' ? Object.entries(m.results) : [];
    for (const [jt, r] of res) {
        if (!r) continue;
        const dl = r.download ? String(r.download).toLowerCase() : null;
        const tf = r.transform ? String(r.transform).toLowerCase() : null;
        if (dl && !MORNING_OK.includes(dl)) return `06:20 check: ${nm(jt)} download ${r.download}`;
        if (tf && !MORNING_OK.includes(tf)) return `06:20 check: ${nm(jt)} transform ${r.transform}`;
        if (Array.isArray(r.missing) ? r.missing.length > 0 : Number(r.missing) > 0) {
            const n = Array.isArray(r.missing) ? r.missing.length : Number(r.missing);
            return `06:20 check: ${nm(jt)} ${n} file${n === 1 ? '' : 's'} missing`;
        }
    }
    return null;
};

// Why a login needs a person, or null: the morning check's note, a turned-off / failing /
// unenrolled / in-fix state, or a test run that failed sign-in counts the same.
export const needsLook = (row, desks, order) => {
    const st = statusOf(row, desks, order);
    const m = row.morning;
    if (row.disabled) return st.label;
    if (row.fixing) return `being fixed on dev since ${fmtDay((row.fixing.at || '').slice(0, 10))}`;
    if (needsEnroll(row)) return 'not signed in on its desk';
    if (m?.note && m.note !== 'clean') return String(m.note);
    const bad = morningProblem(m, row);
    if (bad) return bad;
    if (st.colour === WARN || st.colour === BAD) return st.label;
    if (row.probation && !row.probation.passed_at && row.probation.clean_in_a_row === 0 && row.probation.last_note && row.probation.last_note !== 'clean') {
        return `probation: ${row.probation.last_note}`;
    }
    return null;
};

// Banner: { items: [{id, why}], morning: bool } — morning when any login has a morning entry.
export const bannerItems = (data) => {
    const logins = data?.logins || [];
    const desks = data?.desks || {};
    const order = deskOrder(data);
    const items = logins.map((r) => ({ id: r.id, why: needsLook(r, desks, order) })).filter((x) => x.why);
    // `alert`: stale while it matters (an office desk only inside its logins' windows); older shape: stale
    (Array.isArray(data?.desks_status) ? data.desks_status : []).filter((h) => (h && 'alert' in h ? h.alert : h?.stale)).forEach((h) => {
        items.push({ id: null, desk: h.name, why: `${deskLabel(h.name)}: no heartbeat ${h.at ? `since ${fmtClock(h.at)}` : ''}`.trim() });
    });
    return { items, morning: logins.some((r) => r.morning) };
};

// #login-<id> (a bare token in the hash) -> the id, else null.
export const loginFromHash = (hash) => {
    const m = /^#login-([A-Za-z0-9._-]+)$/.exec(hash || '');
    return m ? m[1] : null;
};
